// מסך 7 — עורך באנר: שכבות, קנבס (דרך BBRuntime), מאפיינים, טיימליין, AI, גרסאות, בעיות, התאמה ותצוגה מקדימה.
// עריכה ידנית (גרירה, מהירות, טקסט) אינה קוראת ל-AI. Autosave עם expectedRevision; השרת הוא מקור האמת.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { BannerDoc } from '@bb/schema';
import { api } from '../api';
import { ErrorBox, fmtDate, Loading, useToast } from '../components/ui';
import Canvas from '../components/editor/Canvas';
import LayersPanel from '../components/editor/LayersPanel';
import PropsPanel, { type LibAsset } from '../components/editor/PropsPanel';
import Timeline from '../components/editor/Timeline';
import AiPanel from '../components/editor/AiPanel';
import { AdaptPanel, IssuesPanel, PreviewModal, VersionsDrawer } from '../components/editor/SidePanels';
import { useDocHistory } from '../components/editor/useDocHistory';
import { clearDraft, readDraft, useAutosave, type SaveStatus } from '../components/editor/useAutosave';
import { SPEC_STATUS_LABEL, type AssetMap, type VariantCtx } from '../components/editor/types';
import '../editor.css';

export default function Editor() {
  const { id } = useParams<{ id: string }>();
  const [ctx, setCtx] = useState<VariantCtx | null>(null);
  const [library, setLibrary] = useState<LibAsset[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const c = await api.get<VariantCtx>(`/api/variants/${id}`);
      setCtx(c);
      setNonce((n) => n + 1);
      api.get<LibAsset[]>(`/api/assets?clientId=${encodeURIComponent(c.campaign.client_id)}`).then(setLibrary).catch(() => setLibrary([]));
    } catch (e: any) { setErr(e.message); }
  }, [id]);
  useEffect(() => { setCtx(null); void load(); }, [load]);

  if (err) return <ErrorBox error={err} />;
  if (!ctx) return <Loading what="טוען עורך" />;
  if (!ctx.revision) return <ErrorBox error="לוריאציה זו אין עדיין גרסה שמורה." />;
  return <EditorInner key={`${ctx.variant.id}-${nonce}`} ctx={ctx} library={library} onReload={load} />;
}

const STATUS_TEXT: Record<SaveStatus, string> = { saved: 'נשמר', dirty: 'שינויים ממתינים…', saving: 'שומר…', error: 'שגיאה – נסה שוב', conflict: 'התנגשות גרסאות', invalid: 'מסמך לא תקין' };
const ZOOMS = [0.25, 0.5, 0.75, 1, 1.5, 2, 3];
type Tab = 'props' | 'ai' | 'sizes' | 'issues';

function EditorInner({ ctx, library, onReload }: { ctx: VariantCtx; library: LibAsset[]; onReload: () => void }) {
  const toast = useToast();
  const vid = ctx.variant.id;
  const hist = useDocHistory(ctx.revision!.doc);
  const doc = hist.doc;
  const save = useAutosave(vid, doc, ctx.revision!.revision);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [snap, setSnap] = useState(true);
  // פתיחה על פריים "מלא" (סוף הלולאה הראשונה או פריים הסיום) כדי שכל השכבות ייראו
  const [elapsed, setElapsed] = useState(() => Math.max(0, (ctx.revision!.doc.endFrameMs ?? ctx.revision!.doc.durationMs) - 1));
  const [playing, setPlaying] = useState(false);
  const [tab, setTab] = useState<Tab>('ai');
  const [showVersions, setShowVersions] = useState(false);
  const [preview, setPreview] = useState<number | null>(null);
  const [narrow, setNarrow] = useState(() => window.innerWidth < 1200);
  const [draft, setDraft] = useState(() => {
    const d = readDraft(vid);
    if (!d) return null;
    // טיוטה מקומית רלוונטית רק אם נבנתה על הגרסה שבשרת כעת
    if (d.baseRevision !== ctx.revision!.revision || JSON.stringify(d.doc) === JSON.stringify(ctx.revision!.doc)) { clearDraft(vid); return null; }
    return d;
  });
  const canvasCol = useRef<HTMLDivElement>(null);

  // מפת נכסים: ספריית הלקוח (להחלפת תמונה/פונט) + נכסי הגרסה
  const assets: AssetMap = useMemo(() => {
    const m: AssetMap = {};
    for (const a of library) m[a.id] = { url: a.url, kind: a.kind, name: a.name, mime: a.mime, family: a.meta?.family, weight: a.meta?.weight, unicodeRange: a.meta?.unicodeRange };
    return { ...m, ...ctx.assets };
  }, [ctx.assets, library]);

  useEffect(() => {
    const h = () => setNarrow(window.innerWidth < 1200);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  // ניגון לפי שעון — הקנבס מבצע seek לכל פריים (דטרמיניסטי)
  useEffect(() => {
    if (!playing) return;
    const total = doc.loops > 0 ? doc.durationMs * doc.loops : Infinity;
    let start = performance.now() - (elapsed >= total ? 0 : elapsed);
    let h = 0;
    const tick = (now: number) => {
      let e = now - start;
      if (e >= total) { setElapsed(total); setPlaying(false); return; }
      if (total === Infinity && e > doc.durationMs * 1000) { start = now; e = 0; }
      setElapsed(e);
      h = requestAnimationFrame(tick);
    };
    h = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing]);

  // קיצורי Undo/Redo — לא בתוך שדה קלט (שם פועל ה-undo של הדפדפן)
  const { undo, redo, apply, breakMerge } = hist;
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (!(e.ctrlKey || e.metaKey)) return;
      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      else if (key === 'y' || (key === 'z' && e.shiftKey)) { e.preventDefault(); redo(); }
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [undo, redo]);

  const change = useCallback((d: BannerDoc, key?: string) => apply(d, key), [apply]);
  const saveAdopt = save.adopt;
  /** אימוץ גרסה שנוצרה בשרת — נכנסת להיסטוריה, כך ש-Ctrl+Z מחזיר את הקודם (ונשמר כגרסה חדשה) */
  const adopt = useCallback((rev: number, d: BannerDoc) => { saveAdopt(rev, d); apply(d); breakMerge(); }, [saveAdopt, apply, breakMerge]);
  const seek = useCallback((ms: number) => { setPlaying(false); setElapsed(ms); }, []);
  const onError = useCallback((m: string) => toast(m, 'err'), [toast]);

  const fitZoom = () => {
    const w = (canvasCol.current?.clientWidth ?? 800) - 40;
    setZoom(Math.max(0.1, Math.min(4, Math.floor(Math.min(w / doc.width, 460 / doc.height) * 100) / 100)));
  };

  const openPreview = async () => {
    const rev = await save.flush();
    if (rev === null) toast('השינויים האחרונים לא נשמרו — התצוגה מציגה את הגרסה השמורה האחרונה', 'err');
    setPreview(rev ?? save.revision);
  };

  const selected = doc.layers.find((l) => l.id === selectedId) ?? null;
  const spec = ctx.spec;
  const specApproved = spec?.status === 'approved';

  return (
    <div className="ed" dir="rtl">
      {narrow && <div className="alert info small" role="note">העורך מיועד למסך מחשב ברוחב 1200 פיקסלים לפחות. במסך צר הפאנלים יוצגו זה מתחת לזה.</div>}
      <header className="ed-top">
        <div className="ed-title">
          <Link to={`/sets/${ctx.set.id}`} className="small">→ סט {ctx.set.name}</Link>
          <h1>{ctx.variant.display_name}</h1>
          <div className="row small">
            <span className="mono badge">{doc.width}×{doc.height}</span>
            <span>{ctx.site.name} · {ctx.placement.name}</span>
            {spec ? (
              <span className={`badge ${specApproved ? 'ok' : 'warn'}`} title={specApproved ? 'מפרט מאושר' : 'מפרט לא מאושר — יצוא כטיוטה בלבד'}>
                מפרט v{spec.version}: {SPEC_STATUS_LABEL[spec.status] ?? spec.status}{!specApproved ? ' · טיוטה בלבד' : ''}
              </span>
            ) : <span className="badge err">אין מפרט</span>}
            {spec?.is_demo && <span className="badge">מפרט הדגמה</span>}
            {ctx.variant.needs_review && <span className="badge warn" title={ctx.variant.needs_review_reason ?? ''}>צריך בדיקה</span>}
          </div>
        </div>
        <div className="ed-status" role="status" aria-live="polite">
          <span className={`save-dot ${save.status}`} aria-hidden="true" />
          <span>{STATUS_TEXT[save.status]}</span>
          <span className="muted small">גרסה <span className="mono">{save.revision}</span></span>
          {save.status === 'error' && <button className="btn small" onClick={() => save.retry()}>נסה שוב</button>}
        </div>
        <div className="row">
          <button className="btn small" onClick={undo} disabled={!hist.canUndo} aria-label="ביטול (Ctrl+Z)" title="Ctrl+Z">↶ ביטול</button>
          <button className="btn small" onClick={redo} disabled={!hist.canRedo} aria-label="חזרה (Ctrl+Y)" title="Ctrl+Y / Ctrl+Shift+Z">↷ חזרה</button>
          <button className="btn small" onClick={() => setShowVersions(true)}>גרסאות</button>
          <button className="btn small primary" onClick={openPreview}>תצוגה מקדימה</button>
          <Link className="btn small" to={`/exports?variantId=${vid}`}>בדיקות ויצוא</Link>
        </div>
      </header>
      {ctx.siblings.length > 1 && (
        <nav className="ed-sibs small" aria-label="גדלים בסט">
          <span>גדלים בסט:</span>
          {ctx.siblings.map((s) => s.id === vid
            ? <span key={s.id} className="badge info mono" aria-current="page">{s.width}×{s.height}</span>
            : <Link key={s.id} to={`/variants/${s.id}/edit`} className="mono">{s.width}×{s.height}</Link>)}
        </nav>
      )}

      {save.status === 'conflict' && (
        <div className="alert err" role="alert">
          <strong>נשמרה בשרת גרסה חדשה יותר{save.conflictRevision ? ` (${save.conflictRevision})` : ''}.</strong> השינויים האחרונים שלכם לא נשמרו ולא דרסו אותה.
          <div className="row" style={{ marginTop: 6 }}>
            <button className="btn small primary" onClick={onReload}>טעינת הגרסה העדכנית</button>
            <button className="btn small" onClick={() => { navigator.clipboard?.writeText(JSON.stringify(doc, null, 2)).then(() => toast('המסמך שלכם הועתק ללוח'), () => toast('ההעתקה נכשלה', 'err')); }}>העתקת המסמך שלי (JSON)</button>
          </div>
        </div>
      )}
      {save.status === 'invalid' && <div className="alert err small" role="alert">המסמך אינו תקין ולא נשמר: {save.error}. אפשר לבטל (Ctrl+Z).</div>}
      {save.status === 'error' && <div className="alert err small" role="alert">השמירה נכשלה: {save.error}. השינויים שמורים מקומית בדפדפן עד שהשמירה תצליח.</div>}
      {draft && (
        <div className="alert info small" role="note">
          נמצאה טיוטה מקומית שלא נשמרה בשרת ({fmtDate(new Date(draft.at).toISOString())}).
          <button className="btn small" style={{ marginInlineStart: 8 }} onClick={() => { apply(draft.doc); setDraft(null); }}>שחזור הטיוטה</button>
          <button className="btn small" style={{ marginInlineStart: 4 }} onClick={() => { clearDraft(vid); setDraft(null); }}>מחיקה</button>
        </div>
      )}

      <div className="ed-main">
        <aside className="ed-left" aria-label="שכבות">
          <LayersPanel doc={doc} selectedId={selectedId} onSelect={(id) => { setSelectedId(id); if (id) setTab('props'); }} onChange={change} />
        </aside>

        <div className="ed-center" ref={canvasCol}>
          <div className="ed-toolbar row small" role="toolbar" aria-label="זום ותצוגה">
            <button className="btn small" aria-label="הקטנת זום" onClick={() => setZoom((z) => [...ZOOMS].reverse().find((x) => x < z) ?? z)}>−</button>
            <select aria-label="זום" value={ZOOMS.includes(zoom) ? String(zoom) : 'custom'} onChange={(e) => setZoom(Number(e.target.value))}>
              {!ZOOMS.includes(zoom) && <option value="custom">{Math.round(zoom * 100)}%</option>}
              {ZOOMS.map((z) => <option key={z} value={String(z)}>{z * 100}%</option>)}
            </select>
            <button className="btn small" aria-label="הגדלת זום" onClick={() => setZoom((z) => ZOOMS.find((x) => x > z) ?? z)}>+</button>
            <button className="btn small" onClick={fitZoom}>התאם למסך</button>
            <button className="btn small" onClick={() => setZoom(1)}>100%</button>
            <label className="chk"><input type="checkbox" checked={snap} onChange={(e) => setSnap(e.target.checked)} /> הצמדה</label>
            <span className="muted small">הזום משנה את התצוגה בלבד.</span>
          </div>
          <Canvas doc={doc} assets={assets} elapsed={elapsed} zoom={zoom} snap={snap} selectedId={selectedId} onSelect={(id) => { setSelectedId(id); if (id) setTab('props'); }} onChange={change} />
        </div>

        <aside className="ed-right" aria-label="כלי עריכה">
          <div className="tabs" role="tablist" aria-label="כלי עריכה">
            {([['ai', 'בקשו שינוי'], ['props', 'עריכה ידנית'], ['sizes', 'גדלים'], ['issues', 'בדיקות']] as [Tab, string][]).map(([t, label]) => (
              <button key={t} role="tab" id={`tab-${t}`} aria-selected={tab === t} aria-controls={`panel-${t}`} className={tab === t ? 'active' : ''} onClick={() => setTab(t)}>{label}</button>
            ))}
          </div>
          <div role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`}>
            {tab === 'props' && <PropsPanel doc={doc} layer={selected} kit={ctx.kit} assets={assets} library={library} campaignCopy={ctx.campaign.copy} onChange={change} />}
            {tab === 'ai' && <AiPanel variantId={vid} revision={save.revision} doc={doc} assets={assets} flush={save.flush} onApplied={adopt} />}
            {tab === 'sizes' && <AdaptPanel variantId={vid} doc={doc} siblings={ctx.siblings} revision={save.revision} flush={save.flush} onAdopt={adopt} />}
            {tab === 'issues' && <IssuesPanel variantId={vid} doc={doc} kit={ctx.kit} flush={save.flush} onSelectLayer={(lid) => { setSelectedId(lid); setTab('props'); }} />}
          </div>
        </aside>
      </div>

      <Timeline doc={doc} elapsed={elapsed} playing={playing} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onStop={() => { setPlaying(false); setElapsed(0); }}
        onSeek={seek} selectedId={selectedId} onSelect={setSelectedId} onChange={change} spec={spec?.resolved ?? null} onError={onError} />

      {showVersions && <VersionsDrawer variantId={vid} revision={save.revision} assets={assets} flush={save.flush} onReverted={(r, d) => { adopt(r, d); toast(`חזרה בוצעה — גרסה ${r}`); }} onClose={() => setShowVersions(false)} />}
      {preview !== null && <PreviewModal variantId={vid} revision={preview} width={doc.width} height={doc.height} onClose={() => setPreview(null)} />}
    </div>
  );
}
